import { useModalMultiple } from '@billy/design-system'
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useFindBankAccountId } from '@features/accounts/hooks/useFindBankAccountId'

import { ModalId } from '../../../enums/modalId'
import { useGetQueryFromEmberRoute } from '../../../hooks/useGetQueryFromEmberRoute'
import { EmberEventFn } from '../../../types/emberEventFn'
import { BankIntegrationModals } from '../elements/BankIntegrationModals/BankIntegrationModals'
import { useOpenBankingAccounts } from '../hooks/useOpenBankingAccounts'
import { useSyncOpenBankingConnection } from '../hooks/useSyncOpenBankingConnection'
import { OpenBankingAccount } from '../types/openBankingAccount'
import { redirectToAiia } from '../utils/redirectToAiia'

interface ContextState {
  accountId: string
  accountCurrency: string
  openBankingAccounts?: OpenBankingAccount[]
  connectedBank?: OpenBankingAccount
  connectedBankAccountId?: string
  hasBankConnectionError: boolean
  isLoading: boolean
  isOrphaned: boolean
  removeConnection: () => void
  renewConnection: (accountId?: string) => void
  requiresBankReconnection: boolean
}

const BankIntegrationContext = createContext<ContextState | undefined>(undefined)

interface BankIntegrationContextProps {
  account?: string
  children?: ReactNode
  onCheckBankIntegration?: EmberEventFn
  organizationId: string
}

export const BankIntegrationContextProvider = ({
  account,
  children,
  onCheckBankIntegration,
  organizationId,
}: BankIntegrationContextProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const [connectedBank, setConnectedBank] = useState<OpenBankingAccount>()
  const [connectedBankAccountId, setConnectedBankAccountId] = useState<string>()

  useGetQueryFromEmberRoute({
    queryParamsValidKeys: [
      'success',
      'connectionId',
      'jobId',
      'error_code',
      'token',
      'auth_code',
      'transactionType',
      'sortProperty',
      'sortDirection',
      'period',
      'status',
      'searchQuery',
    ],
  })

  const { accountId, accountCurrency } = useFindBankAccountId({ account, organizationId })

  const { openBankingAccounts, isLoading: isLoadingOpenBankingAccounts } = useOpenBankingAccounts()

  const connectedBankData = useMemo(
    () => openBankingAccounts?.find((openBankingAccount) => openBankingAccount?.externalAccountId === accountId),
    [accountId, openBankingAccounts],
  )

  const {
    openedIds,
    open: openModal,
    close: closeModal,
  } = useModalMultiple([ModalId.BankSettingsModal, ModalId.BankConnectionSuccessModal])

  const { syncOpenBankingAccountConnection } = useSyncOpenBankingConnection()

  const requiresBankReconnection = connectedBank?.connection?.requiresUpdate || false
  const isBankAccountOrphaned = !!connectedBank?.isOrphaned
  const hasBankConnectionError = !!connectedBank?.connection?.errorCode

  const findConnectedBankData = useCallback(() => {
    if (openBankingAccounts?.length) {
      if (connectedBankData) {
        setConnectedBank(connectedBankData)
        setConnectedBankAccountId(connectedBankData?.id)
      } else {
        setConnectedBank(undefined)
        setConnectedBankAccountId(undefined)
      }

      onCheckBankIntegration?.({ detail: { connectedBankData } })
    } else {
      setConnectedBank(undefined)
      setConnectedBankAccountId(undefined)
      onCheckBankIntegration?.({ detail: { connectedBankData: false } })
    }

    setIsLoading(false)
  }, [openBankingAccounts?.length, connectedBankData, onCheckBankIntegration])

  const handleRenewConnection = useCallback(
    (accountId?: string) => {
      if (!accountId) {
        syncOpenBankingAccountConnection(connectedBankAccountId as string)
      } else {
        const connectedBankId = openBankingAccounts?.find(
          (openBankingAccount) => openBankingAccount?.externalAccountId === accountId,
        )?.id
        syncOpenBankingAccountConnection(connectedBankId as string)
      }
    },
    [syncOpenBankingAccountConnection, connectedBankAccountId, openBankingAccounts],
  )

  const handleRemoveConnection = useCallback(() => {
    redirectToAiia()
  }, [])

  useEffect(() => {
    findConnectedBankData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, openBankingAccounts, connectedBankAccountId])

  useEffect(() => {
    if (requiresBankReconnection && openedIds.length === 0) {
      openModal(ModalId.BankConnectionExpiredModal)
    } else {
      closeModal(ModalId.BankConnectionExpiredModal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, requiresBankReconnection])

  return (
    <BankIntegrationContext.Provider
      value={{
        accountId,
        openBankingAccounts,
        connectedBank,
        connectedBankAccountId,
        accountCurrency,
        hasBankConnectionError,
        isLoading: isLoading || isLoadingOpenBankingAccounts,
        isOrphaned: isBankAccountOrphaned,
        removeConnection: handleRemoveConnection,
        renewConnection: handleRenewConnection,
        requiresBankReconnection,
      }}
    >
      {children}
      <BankIntegrationModals removeConnection={handleRemoveConnection} />
    </BankIntegrationContext.Provider>
  )
}

export const useBankIntegration = () => {
  const context = useContext(BankIntegrationContext)

  if (!context) {
    throw new Error('BankIntegrationContextProvider is missing in the module!')
  }

  return context
}
